import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { get, post } from '@/api/client'
import { Icon } from '@/icons/Icon'
import { Loader } from '@/components/Loader'
import { whenText } from '@/lib/time'

import '@/styles/pages/settings.css'

interface NotificationOut {
  id: string
  type: string
  title: string
  body: string | null
  icon: string
  tone: string
  href: string | null
  entityType: string | null
  entityId: string | null
  readAt: string | null
  createdAt: string | null
}

interface Page<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

const POLL_MS = 30_000
const TONES = new Set(['brand', 'info', 'success', 'warning', 'error'])

/** The app-wide "when" rule (lib/time): "just now" · "6m ago" · "3h ago" ·
    "Yesterday at 3:49 PM" · the full date after that, in the viewer's time zone. */
export function relativeTime(value: string | null | undefined): string {
  return value ? whenText(value) : ''
}

export function NotificationBell() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: POLL_MS,
    retry: false,
    staleTime: 0,
  })

  const list = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => get<Page<NotificationOut>>('/notifications', { page: 1, pageSize: 12 }),
    enabled: open,
    retry: false,
    staleTime: 0,
  })

  const refetchCount = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
  }, [qc])

  // Poll on window focus too, so the badge is right when you come back to the tab.
  useEffect(() => {
    window.addEventListener('focus', refetchCount)
    return () => window.removeEventListener('focus', refetchCount)
  }, [refetchCount])

  // Outside click and Escape both close the panel.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btnRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const markRead = useMutation({
    mutationFn: (id: string) => post<unknown>(`/notifications/${id}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAll = useMutation({
    mutationFn: () => post<unknown>('/notifications/read-all'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const count = unread.data?.count ?? 0
  const items = list.data?.items ?? []

  const openItem = (n: NotificationOut) => {
    if (!n.readAt) markRead.mutate(n.id)
    setOpen(false)
    if (n.href) navigate(n.href)
  }

  return (
    <div className="nb" ref={rootRef}>
      <button
        className={open ? 'nb__btn is-open' : 'nb__btn'}
        type="button"
        ref={btnRef}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={18} />
        {count > 0 ? <span className="nb__badge">{count > 9 ? '9+' : count}</span> : null}
      </button>

      {open ? (
        <div className="nb__panel" role="dialog" aria-label="Notifications">
          <div className="nb__head">
            <span className="nb__title">Notifications</span>
            <button
              className="nb__link"
              type="button"
              disabled={markAll.isPending || count === 0}
              onClick={() => markAll.mutate()}
            >
              Mark all read
            </button>
          </div>

          <div className="nb__list">
            {list.isLoading ? (
              <div className="nb__pad" style={{ display: 'flex', justifyContent: 'center' }}>
                <Loader size="sm" label="Loading notifications" />
              </div>
            ) : list.isError ? (
              <div className="nb__pad">
                <div className="ix-empty">
                  <div className="ix-empty__title">Notifications are unavailable</div>
                  <div className="ix-empty__body">Please try again in a moment.</div>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="nb__pad">
                <div className="ix-empty">
                  <span className="ix-icon-tile ix-icon-tile--brand">
                    <Icon name="check-circle" size={18} />
                  </span>
                  <div className="ix-empty__title">You are all caught up</div>
                  <div className="ix-empty__body">
                    New circulars, deadlines and task updates will show up here.
                  </div>
                </div>
              </div>
            ) : (
              items.map((n) => {
                const tone = TONES.has(n.tone) ? n.tone : 'brand'
                return (
                  <button
                    className={n.readAt ? 'nb__item' : 'nb__item is-unread'}
                    type="button"
                    key={n.id}
                    onClick={() => openItem(n)}
                  >
                    <span className={`nb__tile nb__tile--${tone}`}>
                      <Icon name={n.icon || 'bell'} size={15} />
                    </span>
                    <span className="nb__id">
                      <span className="nb__t">{n.title}</span>
                      {n.body ? <span className="nb__b">{n.body}</span> : null}
                      <span className="nb__time">{relativeTime(n.createdAt)}</span>
                    </span>
                    {!n.readAt ? <span className="nb__dot" aria-label="Unread" /> : null}
                  </button>
                )
              })
            )}
          </div>

          <div className="nb__foot">
            <Link className="nb__link" to="/settings/notifications" onClick={() => setOpen(false)}>
              Notification settings
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default NotificationBell
